import React from 'react';

import { Box, Chip, CircularProgress, TextField, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import { FiBarChart2, FiCheckCircle, FiEdit3, FiMinusCircle, FiUser } from 'react-icons/fi';

import { AppCheckbox, AppRoundedRadio, AppSearchInput, AppSwitch } from '../../../components/inputs';
import { WorkerPickerRow } from '../../../components/WorkerPickerRow';
import { SettingsStatusPill } from '../../../components/SettingsCard/SettingsStatusPill';
import { Team, TeamMember } from '../../../types';
import {
  ANALYSIS_REQUEST_MAX,
  INDIVIDUAL_SECTIONS,
  MAX_PEOPLE,
  MAX_RECIPIENTS,
  SECTION_PRESETS,
  SNAPSHOT_STATS,
} from '../constants';
import { RecipientOption, RequestReportWizardApi } from '../hooks/useRequestReportWizard';
import { useReportStyles } from '../styles';
import { containsSensitiveSections } from '../utils';
import { ReportOptionCard } from './ReportOptionCard';

interface StepProps {
  wizard: RequestReportWizardApi;
}

const toWorker = (member: TeamMember) => ({
  user_id: member.user_id,
  first_name: member.first_name,
  last_name: member.last_name,
  avatar: member.avatar || null,
  ni_number: member.ni_number || null,
  modules: [],
  email: member.email || null,
  phone: member.phone || null,
});

const RadioLine: React.FC<{ label: string; checked: boolean; onSelect: () => void }> = ({
  label,
  checked,
  onSelect,
}) => (
  <Box display="flex" alignItems="center" gridGap={4} style={{ cursor: 'pointer' }} onClick={onSelect}>
    <AppRoundedRadio checked={checked} inputProps={{ 'aria-label': label }} />
    <Typography variant="body2">{label}</Typography>
  </Box>
);

/* ---------- 1. Type ---------- */

export const TypeStep: React.FC<StepProps> = ({ wizard }) => {
  const classes = useReportStyles();
  const { reportType } = wizard.form;

  return (
    <Box className={classes.typeGrid} role="radiogroup" aria-label="Report type">
      <ReportOptionCard
        className={classes.typeCard}
        icon={<FiBarChart2 size={18} />}
        title="Company or team snapshot"
        description="Headcount, roles, nationalities, RTW/ID and qualification coverage for the whole company or chosen teams. Numbers only."
        selected={reportType === 'company_snapshot'}
        onSelect={() => wizard.setReportType('company_snapshot')}>
        <Box className={classes.pillRow} marginTop={1}>
          <SettingsStatusPill label="No personal data · shareable" tone="on" />
        </Box>
      </ReportOptionCard>
      <ReportOptionCard
        className={classes.typeCard}
        icon={<FiUser size={18} />}
        title="Individual report"
        description="A detailed record on named people: profile, qualifications and cards, and (opt-in) personal or ID/RTW detail."
        selected={reportType === 'individual'}
        onSelect={() => wizard.setReportType('individual')}>
        <Box className={classes.pillRow} marginTop={1}>
          <SettingsStatusPill label="Contains personal data" tone="warning" />
        </Box>
      </ReportOptionCard>
    </Box>
  );
};

/* ---------- 2a. Snapshot scope ---------- */

export const ScopeStep: React.FC<StepProps & { teams: Team[] }> = ({ wizard, teams }) => {
  const classes = useReportStyles();
  const { wholeCompany, teamIds } = wizard.form;

  return (
    <Box>
      <Typography className={classes.fieldLabel}>Who should this snapshot cover?</Typography>
      <RadioLine label="Whole company" checked={wholeCompany} onSelect={() => wizard.update('wholeCompany', true)} />
      <RadioLine
        label="One or more specific teams"
        checked={!wholeCompany}
        onSelect={() => wizard.update('wholeCompany', false)}
      />
      {!wholeCompany && (
        <Box marginTop={1.5} marginLeft={1}>
          {teams.length ? (
            teams.map(team => (
              <Box key={team.uuid} className={classes.checkRow} onClick={() => wizard.toggleTeam(team.uuid)}>
                <Typography variant="body2">
                  {team.name}
                  <Typography component="span" className={classes.hint}>
                    {' '}
                    · {team.member_count} {team.member_count === 1 ? 'member' : 'members'}
                  </Typography>
                </Typography>
                <AppCheckbox
                  checked={teamIds.includes(team.uuid)}
                  color="primary"
                  variant="rectangle"
                  onClick={event => event.stopPropagation()}
                  onChange={() => wizard.toggleTeam(team.uuid)}
                  inputProps={{ 'aria-label': team.name }}
                />
              </Box>
            ))
          ) : (
            <Typography className={classes.empty}>No teams yet.</Typography>
          )}
        </Box>
      )}
      <Typography className={classes.note}>
        Team proximity uses the locations linked to these teams; the report lists them.
      </Typography>
    </Box>
  );
};

/* ---------- 2b. Individual: people ---------- */

export const PeopleStep: React.FC<StepProps> = ({ wizard }) => {
  const classes = useReportStyles();
  const selectedIds = wizard.form.userIds;

  return (
    <Box>
      <Typography className={classes.fieldLabel}>Search and select team members</Typography>
      <AppSearchInput
        value={wizard.peopleSearch}
        onChange={event => wizard.setPeopleSearch(event.target.value)}
        placeholder="Search team members"
        isSmall
        smallRounding
        isBorder
        expanded
        fullWidth
      />
      <Box className={classes.peopleList}>
        {wizard.peopleLoading && !wizard.people.length ? (
          <Box display="flex" justifyContent="center" padding={3}>
            <CircularProgress size={24} />
          </Box>
        ) : wizard.people.length ? (
          wizard.people.map(person => {
            const checked = selectedIds.includes(person.user_id);
            return (
              <WorkerPickerRow
                key={person.user_id}
                worker={toWorker(person)}
                checked={checked}
                disabled={!checked && selectedIds.length >= MAX_PEOPLE}
                disabledTooltip={`Up to ${MAX_PEOPLE} people per report`}
                subtitle={person.email || person.phone || undefined}
                checkboxVariant="rectangle"
                onToggle={() => wizard.togglePerson(person)}
              />
            );
          })
        ) : (
          <Typography className={classes.empty}>No team members match this search.</Typography>
        )}
      </Box>
      {wizard.selectedPeople.length === 0 ? (
        <Typography className={classes.hint}>
          No one selected yet — pick at least one team member to continue.
        </Typography>
      ) : (
        <Box className={classes.chips}>
          {wizard.selectedPeople.map(person => (
            <Chip
              key={person.user_id}
              className={classes.chip}
              label={`${person.first_name} ${person.last_name}`.trim()}
              onDelete={() => wizard.togglePerson(person)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/* ---------- 3. Individual: content ---------- */

export const ContentStep: React.FC<StepProps> = ({ wizard }) => {
  const classes = useReportStyles();
  const { sections } = wizard.form;

  return (
    <Box>
      <Box className={classes.presetRow} role="radiogroup" aria-label="Presets">
        {SECTION_PRESETS.map(preset => (
          <ReportOptionCard
            key={preset.key}
            className={classes.preset}
            title={preset.label}
            selected={wizard.activePreset === preset.key}
            onSelect={() => wizard.applyPreset(preset.key)}
          />
        ))}
      </Box>
      <Typography className={classes.fieldLabel}>Or choose sections one by one</Typography>
      {INDIVIDUAL_SECTIONS.map(section => {
        const allowed = wizard.isSectionAllowed(section.value);
        const permission =
          section.value === 'id_rtw' ? 'teams:can_view_worker_documents' : 'teams:can_export_personal_info';
        return (
          <Box key={section.value} className={classes.toggleRow}>
            <Box>
              <Typography
                className={clsx(classes.optionTitle, { [classes.sensitiveTitle]: section.sensitive })}>
                {section.label}
              </Typography>
              <Typography className={classes.hint}>
                {allowed ? section.hint : `Needs the ${permission} permission.`}
              </Typography>
            </Box>
            <AppSwitch
              checked={sections.includes(section.value)}
              disabled={!allowed}
              onChange={() => wizard.toggleSection(section.value)}
              inputProps={{ 'aria-label': section.label }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

/* ---------- 4. Individual: qualifications ---------- */

export const QualificationsStep: React.FC<StepProps> = ({ wizard }) => {
  const classes = useReportStyles();
  const { allQualifications, qualificationTitles } = wizard.form;

  return (
    <Box>
      <Typography className={classes.fieldLabel}>Which qualifications should be included?</Typography>
      <RadioLine
        label="All qualifications held"
        checked={allQualifications}
        onSelect={() => wizard.update('allQualifications', true)}
      />
      <RadioLine
        label="A specific set"
        checked={!allQualifications}
        onSelect={() => wizard.update('allQualifications', false)}
      />
      {!allQualifications && (
        <Box marginTop={1.5}>
          {wizard.titlesLoading ? (
            <Box display="flex" justifyContent="center" padding={2}>
              <CircularProgress size={24} />
            </Box>
          ) : wizard.titles.length ? (
            <Box className={classes.chips}>
              {wizard.titles.map(title => {
                const selected = qualificationTitles.includes(title);
                return (
                  <Chip
                    key={title}
                    label={title}
                    clickable
                    className={clsx(classes.chip, { [classes.chipIdle]: !selected })}
                    onClick={() => wizard.toggleTitle(title)}
                    aria-pressed={selected}
                  />
                );
              })}
            </Box>
          ) : (
            <Typography className={classes.empty}>
              The selected people hold no qualifications yet — the section will be left out.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

/* ---------- 5. Analysis ---------- */

export const AnalysisStep: React.FC<StepProps> = ({ wizard }) => {
  const classes = useReportStyles();
  const { analysisMode, requestText } = wizard.form;

  return (
    <Box display="flex" flexDirection="column" gridGap={10} role="radiogroup" aria-label="Analysis">
      <Typography className={classes.fieldLabel}>Would you like this report analysed?</Typography>
      <ReportOptionCard
        icon={<FiMinusCircle size={16} />}
        title="No analysis — just the data"
        description="The report only, with no written analysis added."
        selected={analysisMode === 'none'}
        onSelect={() => wizard.update('analysisMode', 'none')}
      />
      <ReportOptionCard
        icon={<FiCheckCircle size={16} />}
        title="Building Safety Regulations compliance"
        description="Checks this data against the latest Building Safety Regulations and adds a red / amber / green rating on the areas that need attention."
        selected={analysisMode === 'preset'}
        onSelect={() => wizard.update('analysisMode', 'preset')}>
        <Box marginTop={1}>
          <SettingsStatusPill label="Vyce preset" />
        </Box>
      </ReportOptionCard>
      <ReportOptionCard
        icon={<FiEdit3 size={16} />}
        title="Write your own request"
        description="Describe what you want analysed in your own words. Our AI adds the answer to the end of the report."
        selected={analysisMode === 'custom'}
        onSelect={() => wizard.update('analysisMode', 'custom')}>
        {analysisMode === 'custom' && (
          <Box marginTop={1.5} onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
            <TextField
              autoFocus
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              variant="outlined"
              placeholder="e.g. Who needs chasing for expiring cards before year end?"
              value={requestText}
              onChange={event => wizard.update('requestText', event.target.value.slice(0, ANALYSIS_REQUEST_MAX))}
              inputProps={{ 'aria-label': 'Your analysis request', maxLength: ANALYSIS_REQUEST_MAX }}
            />
            <Typography className={classes.hint} align="right" style={{ marginTop: 4 }}>
              {requestText.length} / {ANALYSIS_REQUEST_MAX}
            </Typography>
          </Box>
        )}
      </ReportOptionCard>
    </Box>
  );
};

/* ---------- 6. Review & generate ---------- */

const SECTION_LABELS = Object.fromEntries(INDIVIDUAL_SECTIONS.map(section => [section.value, section.label]));

export const ReviewStep: React.FC<StepProps & { teams: Team[]; companyName?: string }> = ({
  wizard,
  teams,
  companyName,
}) => {
  const classes = useReportStyles();
  const { form } = wizard;
  const isSnapshot = form.reportType === 'company_snapshot';

  const scopeText = isSnapshot
    ? form.wholeCompany
      ? 'whole company'
      : teams
          .filter(team => form.teamIds.includes(team.uuid))
          .map(team => team.name)
          .join(', ')
    : `${form.userIds.length} team ${form.userIds.length === 1 ? 'member' : 'members'} — ${wizard.selectedPeople
        .map(person => `${person.first_name} ${person.last_name}`.trim())
        .join(', ')}`;

  return (
    <Box>
      <Box className={classes.summaryBox}>
        Scope: <b>{scopeText}</b>
        {companyName ? ` · ${companyName}` : ''}
      </Box>

      {isSnapshot ? (
        <>
          <Typography className={classes.fieldLabel}>Choose what to include</Typography>
          {SNAPSHOT_STATS.map(stat => (
            <Box key={stat.value} className={classes.checkRow} onClick={() => wizard.toggleStat(stat.value)}>
              <Typography variant="body2">{stat.label}</Typography>
              <AppCheckbox
                checked={form.stats.includes(stat.value)}
                color="primary"
                variant="rectangle"
                onClick={event => event.stopPropagation()}
                onChange={() => wizard.toggleStat(stat.value)}
                inputProps={{ 'aria-label': stat.label }}
              />
            </Box>
          ))}
          <Typography className={classes.note}>
            Counts and percentages only. Below 10 team members we show raw counts alongside percentages.
          </Typography>
        </>
      ) : (
        <>
          <Typography className={classes.fieldLabel}>Content included</Typography>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            {form.sections
              .map(section =>
                section === 'qualifications' && !form.allQualifications
                  ? `${SECTION_LABELS[section]} (${form.qualificationTitles.length} selected)`
                  : SECTION_LABELS[section]
              )
              .join(' · ')}
          </Typography>

          <Typography className={classes.fieldLabel}>Field set</Typography>
          <Box display="flex" flexDirection="column" gridGap={8} role="radiogroup" aria-label="Field set">
            <ReportOptionCard
              title="Lean (recommended)"
              description="Only the fields needed for the sections above."
              selected={form.fieldSet === 'lean'}
              onSelect={() => wizard.update('fieldSet', 'lean')}
            />
            <ReportOptionCard
              title="Full details"
              description={
                wizard.canExportPersonal
                  ? 'Adds the remaining opt-in fields, such as email, mobile and date of birth.'
                  : 'Needs the teams:can_export_personal_info permission.'
              }
              selected={form.fieldSet === 'full'}
              disabled={!wizard.canExportPersonal}
              onSelect={() => wizard.update('fieldSet', 'full')}
            />
          </Box>
          {(containsSensitiveSections(form.sections) || form.fieldSet === 'full') && (
            <Typography className={classes.note}>
              This report includes personal or ID/RTW data that will leave the platform. Every report is
              logged for your organisation’s audit trail.
            </Typography>
          )}
        </>
      )}

      <Box marginTop={3}>
        <Typography className={classes.fieldLabel}>When it’s ready, tell me by</Typography>
        <Box display="flex" gridGap={24} flexWrap="wrap">
          {(
            [
              ['email', 'Email'],
              ['messaging', 'Vyce Messaging'],
            ] as const
          ).map(([channel, label]) => (
            <Box
              key={channel}
              display="flex"
              alignItems="center"
              style={{ cursor: 'pointer' }}
              onClick={() => wizard.toggleChannel(channel)}>
              <AppCheckbox
                checked={form.channels.includes(channel)}
                color="primary"
                variant="rectangle"
                onClick={event => event.stopPropagation()}
                onChange={() => wizard.toggleChannel(channel)}
                inputProps={{ 'aria-label': label }}
              />
              <Typography variant="body2">{label}</Typography>
            </Box>
          ))}
        </Box>
        {form.channels.length ? (
          <Box marginTop={1.5}>
            <Autocomplete<RecipientOption, true>
              multiple
              options={wizard.recipientOptions}
              value={wizard.selectedRecipients}
              loading={wizard.recipientsLoading}
              filterOptions={options => options}
              getOptionLabel={option => option.name}
              getOptionSelected={(option, value) => option.uuid === value.uuid}
              onChange={(_, value) => wizard.changeRecipients(value)}
              onInputChange={(_, value, reason) => reason === 'input' && wizard.setRecipientSearch(value)}
              getOptionDisabled={() => wizard.selectedRecipients.length >= MAX_RECIPIENTS}
              noOptionsText="No Teams admins or managers match"
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label="Also share with (optional)"
                />
              )}
            />
            {/* Not helperText: the theme positions it absolutely, so it would overlap what follows */}
            <Typography className={classes.hint} style={{ marginTop: 4 }}>
              Teams admins and managers who can request reports. Up to {MAX_RECIPIENTS}.
            </Typography>
          </Box>
        ) : (
          <Typography className={classes.note}>
            No one will be notified. The report will still appear on the Reports page.
          </Typography>
        )}
      </Box>

      <Box marginTop={4}>
        <Typography className={classes.fieldLabel}>Analysis</Typography>
        <Typography variant="body2">
          {form.analysisMode === 'none' && 'No written analysis — data only.'}
          {form.analysisMode === 'preset' &&
            'Building Safety Regulations compliance — a red / amber / green rating is added at the end.'}
          {form.analysisMode === 'custom' && `Custom request — “${form.requestText.trim()}”`}
        </Typography>
      </Box>

      {wizard.submitError && (
        <Box className={classes.alert} role="alert">
          <Typography className={classes.alertTitle}>{wizard.submitError.title}</Typography>
          {wizard.submitError.description && (
            <Typography className={classes.alertText}>{wizard.submitError.description}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
